/* eslint-disable max-lines-per-function */
import { Formik, FormikConfig } from "formik";
import React from "react";
import { Grid } from "@material-ui/core";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { Checkbox, Form, SubmitButton } from "components/haven-design/form";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import {
  SelectInterestList,
  SelectOrganization,
} from "components/haven-design/form/special-fields";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { extendedStatesList } from "components/haven-design/state-picker";
import { PropertyCreateInput } from "types/gql-types";
import styles from "../adminDashboard.module.css";

export type Property = PropertyCreateInput;

interface Props {
  initialData: Property;
  formType: "update" | "new";
  handleSubmit: FormikConfig<Property>["onSubmit"];
  sending: boolean;
}

// https://github.com/jquense/yup
export const formSchema = y.object().shape({
  __typename: y.string().strip(true),

  id: y.string().strip(true),

  organizationId: y
    .string()
    .trim()
    .required(),

  name: y
    .string()
    .trim()
    .max(40)
    .required(),

  phone: y
    .string()
    .trim()
    .required(),

  phoneExt: y
    .string()
    .trim()
    .nullable(),

  email: y
    .string()
    .trim()
    .lowercase()
    .required(),

  interestListId: y
    .string()
    .trim()
    .required(),

  address: y.object().shape({
    __typename: y.string().strip(true),

    address1: y
      .string()
      .trim()
      .required(),

    address2: y
      .string()
      .nullable()
      .trim(),

    city: y
      .string()
      .trim()
      .required(),

    state: y
      .string()
      .trim()
      .required(),

    zip: y
      .string()
      .trim()
      .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Incorrect zipcode format")
      .required(),
  }),

  submissionDigestEnabled: y.boolean(),

  invoicedCustomer: y.object().shape({
    __typename: y.string().strip(true),
  }),
});

const PropertiesForm: React.FC<Props> = (props: Props) => (
  <div className={`${styles.formWidth}`}>
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={formSchema}>
      {({ isSubmitting, values }) => (
        <Form>
          <Grid container spacing={2} className={`card ${styles.formBottom}`}>
            <p>Required fields marked with *</p>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <SelectOrganization name="organizationId" label="Organization *" showLabel />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <SelectInterestList
                  label="Associated Interest List *"
                  name="interestListId"
                  isWaitlist
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Property Name *" name="name" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Property Contact Phone *" name="phone" type="text" />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Phone Extension" name="phoneExt" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Property Contact Email *" name="email" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Checkbox
                  label="Receive Daily Submission Email Digest"
                  name="submissionDigestEnabled"
                  checked={!!values.submissionDigestEnabled}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Address *" name="address.address1" type="text" />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Address 2" name="address.address2" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="City *" name="address.city" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <MaterialFormikSelectAutocomplete
                  margin="none"
                  id="address.state"
                  name="address.state"
                  placeholder="State *"
                  title="State *"
                  options={extendedStatesList}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Zip *" name="address.zip" type="text" />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Checkbox
                  label="Enable Invoiced Billing"
                  name="invoicedCustomer.active"
                  checked={!!values.invoicedCustomer?.active}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12} sm={8} md={6}>
                <Input margin="none" label="Invoiced Customer Number" name="invoicedCustomer.invoicedId" type="text" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}><br/></Grid>
          <Grid item xs={12}>
            <HistoryBackButton marginRight size="L" />

            <SubmitButton isWide text="Submit" loadingText="Sending" isLoading={isSubmitting} />
          </Grid>
        </Form>
      )}
    </Formik>
  </div>
);

export default PropertiesForm;
